import SourcesRoundsAsSourceSelector from "../Sources/SourcesRoundsAsSourceSelector";
import {
  Text,
  Box,
  Button,
  Switch,
  Collapse,
  HStack,
  Checkbox,
  VStack,
  Tag,
  TagLabel,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import { getFolderForRound, KB_FOLDER, getFileType } from "api/common/fileinfo";
import { useQuery } from "react-query";
import fileinfoService from "api/common/fileinfo.service";
import FoundationSourceSelector from "./FoundationSourceSelector";
import { isModuleAvailable } from "utils/AuthUtils";
import MODULES from "config/modules";
import { useAuth } from "context";
import { useRightSidebar } from "context/RightSidebarContext";
import SourcesUploadRSidebar from "../Sources/SourcesUploadRSidebar";
import questionnaireService from "../../../api/issb/questionnaire.service";
import { issbKeys } from "hooks/query";
import Issb from "api/issb/issb";
import { FilePdf, FileDoc, FileXls, FileCsv, FilePpt, FileText, GlobeSimple, File } from "@phosphor-icons/react";

const ROUND_STAGE_OPTIONS = [
  { value: "IN_PROGRESS", label: "In Progress" },
  { value: "INTERNAL_REVIEW", label: "Internal Review" },
  { value: "AUDIT", label: "Audit" },
  { value: "COMPLETE", label: "Complete" },
];

const FILE_TYPE_ICONS = {
  PDF: FilePdf,
  DOCX: FileDoc,
  DOC: FileDoc,
  XLSX: FileXls,
  XLS: FileXls,
  CSV: FileCsv,
  PPTX: FilePpt,
  PPT: FilePpt,
  TXT: FileText,
  URL: GlobeSimple,
};
const getFileIcon = (fileInfo) => {
  const type = getFileType(fileInfo)?.toUpperCase();
  const Icon = FILE_TYPE_ICONS[type] || File;
  return <Icon size={14} />;
};

/** must pick/confirm sources before proceeding */
export default function AIReviewSourcesPanel({
  setNextDisabled,
  sourceIds,
  setSourceIds,
  hideTips,
  sourceRoundIds,
  setSourceRoundIds,
  foundationQuestionIds,
  setFoundationQuestionIds,
  foundationEnabled,
  setFoundationEnabled,
  documentsEnabled,
  setDocumentsEnabled,
  otherRoundsEnabled,
  setOtherRoundsEnabled,
  roundQuestionIds,
  setRoundQuestionIds,
}) {
  const roundSelector = useSelector((state) => state.issbRound);
  const { getAccessTokenSilently } = useAuth();
  const { setRightSidebar, doOpen } = useRightSidebar();
  const { data: filesPage } = useQuery(["fileinfoService.list", "all"], async () => {
    await getAccessTokenSilently();
    return fileinfoService.list();
  });
  // HACK: add KB to the list of files
  const { data: filesKBPage } = useQuery(["fileinfoService.list", KB_FOLDER], async () => {
    await getAccessTokenSilently();
    return fileinfoService.list({ query: "folder:" + KB_FOLDER });
  });

  const questionnaireId = roundSelector?.selectedRound?.id;

  // Fetch questionnaire to get sourceRoundIds
  // HACK: query key is reset by SourcesListPanel
  const { data: questionnaireData } = useQuery(
    issbKeys.questionnaireRound(questionnaireId),
    async () => {
      await getAccessTokenSilently();
      return questionnaireService.get(questionnaireId);
    },
    {
      enabled: !!questionnaireId,
    }
  );

  // Build active files list (round + KB)
  const roundId = roundSelector?.selectedRound?.id;
  const activeFiles = useRef([]);
  if (filesPage?.content && roundId) {
    const folder = getFolderForRound(roundId);
    const filesInRound = filesPage.content.filter((f) => f.folders?.includes(folder));
    const kbFiles = filesKBPage?.content || [];
    const roundFileIds = new Set(filesInRound.map((f) => f.id));
    const combined = [...filesInRound, ...kbFiles.filter((f) => !roundFileIds.has(f.id))];
    activeFiles.current = combined;
  }

  // Select all files in round + KB files once on initial load
  const initRoundIdRef = useRef(null);
  useEffect(() => {
    if (!roundId) return;
    if (!filesPage?.content) return;
    if (initRoundIdRef.current === roundId) return;
    initRoundIdRef.current = roundId;

    setSourceIds(activeFiles.current.map((f) => f.id));
  }, [filesPage?.content, filesKBPage?.content, roundId, setSourceIds]);

  const gotoUpload = () => {
    setRightSidebar(
      <SourcesUploadRSidebar activeFiles={activeFiles.current} hideAIReviewButton={true} tables={["ACTIVE"]} />
    );
    doOpen();
  };

  const handleToggleFile = (fileId) => {
    setSourceIds((prev) => (prev.includes(fileId) ? prev.filter((id) => id !== fileId) : [...prev, fileId]));
  };

  // Round stage filtering
  const [roundStages, setRoundStages] = useState(() => ROUND_STAGE_OPTIONS.map((s) => s.value));

  // Fetch questions for all selected rounds
  const roundIdsKey = sourceRoundIds?.join(",") || "";
  const { data: roundQuestions } = useQuery(
    ["roundQuestions", roundIdsKey],
    async () => {
      await getAccessTokenSilently();
      const results = await Promise.all(sourceRoundIds.map((id) => Issb.getQuestionsOfRound({ id, paramsString: "" })));
      return results.flat();
    },
    { enabled: sourceRoundIds?.length > 0 }
  );

  const presentRoundStages = useMemo(() => {
    if (!roundQuestions) return new Set();
    return new Set(roundQuestions.map((q) => q.stageType));
  }, [roundQuestions]);

  // Sync roundQuestionIds when rounds or stages change
  useEffect(() => {
    if (!roundQuestions || sourceRoundIds?.length === 0) {
      setRoundQuestionIds([]);
      return;
    }
    const filtered = roundQuestions.filter((q) => roundStages.includes(q.stageType));
    setRoundQuestionIds(filtered.map((q) => q.id));
  }, [roundQuestions, roundStages, sourceRoundIds, setRoundQuestionIds]);

  const handleToggleRoundStage = (stageValue) => {
    setRoundStages((prev) =>
      prev.includes(stageValue) ? prev.filter((s) => s !== stageValue) : [...prev, stageValue]
    );
  };

  const foundationModuleAvailable = isModuleAvailable(MODULES.FoundationData.id);

  // Validation: at least one enabled toggle must have selections
  useEffect(() => {
    const foundationValid = foundationModuleAvailable && foundationEnabled && foundationQuestionIds?.length > 0;
    const documentsValid = documentsEnabled && sourceIds?.length > 0;
    const roundsValid = otherRoundsEnabled && sourceRoundIds?.length > 0;

    const anyEnabled = (foundationModuleAvailable && foundationEnabled) || documentsEnabled || otherRoundsEnabled;
    const anyValid = foundationValid || documentsValid || roundsValid;

    setNextDisabled(!anyEnabled || !anyValid);
  }, [
    sourceIds,
    sourceRoundIds,
    foundationQuestionIds,
    foundationEnabled,
    foundationModuleAvailable,
    documentsEnabled,
    otherRoundsEnabled,
    setNextDisabled,
  ]);

  return (
    <Box>
      <Text mb={4}>Select the sources you want Antaia AI to use when drafting answers.</Text>

      {/* Foundation Data */}
      {foundationModuleAvailable && (
        <Box
          mb={3}
          p={4}
          borderWidth="1px"
          borderColor={foundationEnabled ? "lime" : "gray.200"}
          borderRadius="xl"
          bg="white"
          transition="border-color 0.2s"
        >
          <HStack spacing={3} align="flex-start">
            <Switch
              size="sm"
              mt="5px"
              colorScheme="limeShade"
              isChecked={foundationEnabled}
              onChange={(e) => setFoundationEnabled(e.target.checked)}
            />
            <Box flex={1}>
              <Text fontSize="body.m" fontWeight="bold">
                Foundation Data
              </Text>
              <Text fontSize="sm" color="gray.500">
                Use your ESG Actions data as reference
              </Text>
              <Collapse in={foundationEnabled} animateOpacity>
                <Box mt={3}>
                  <FoundationSourceSelector
                    selectedQuestionIds={foundationQuestionIds}
                    setSelectedQuestionIds={setFoundationQuestionIds}
                  />
                </Box>
              </Collapse>
            </Box>
          </HStack>
        </Box>
      )}

      {/* Document Sources */}
      <Box
        mb={3}
        p={4}
        borderWidth="1px"
        borderColor={documentsEnabled ? "lime" : "gray.200"}
        borderRadius="xl"
        bg="white"
        transition="border-color 0.2s"
      >
        <HStack spacing={3} align="flex-start">
          <Switch
            size="sm"
            mt="5px"
            colorScheme="limeShade"
            isChecked={documentsEnabled}
            onChange={(e) => setDocumentsEnabled(e.target.checked)}
          />
          <Box flex={1}>
            <Text fontSize="body.m" fontWeight="bold">
              Document Sources
              {documentsEnabled && sourceIds?.length > 0 && (
                <Text as="span" fontWeight="normal" color="gray.500">
                  {" "}
                  ({sourceIds.length})
                </Text>
              )}
            </Text>
            <Text fontSize="sm" color="gray.500">
              Include uploaded documents for AI reference
            </Text>
            <Collapse in={documentsEnabled} animateOpacity>
              <Box
                mt={3}
                maxH="300px"
                overflowY="auto"
                sx={{
                  "&::-webkit-scrollbar": { width: "6px" },
                  "&::-webkit-scrollbar-thumb": { bg: "transparent", borderRadius: "full" },
                  "&:hover::-webkit-scrollbar-thumb": { bg: "gray.300" },
                  scrollbarWidth: "thin",
                  scrollbarColor: "transparent transparent",
                  "&:hover": { scrollbarColor: "var(--chakra-colors-gray-300) transparent" },
                }}
              >
                <VStack spacing={0} align="stretch">
                  {activeFiles.current.map((file) => {
                    const isSelected = sourceIds.includes(file.id);
                    return (
                      <HStack
                        key={file.id}
                        py={2}
                        px={2}
                        cursor="pointer"
                        onClick={() => handleToggleFile(file.id)}
                        _hover={{ bg: "gray.100" }}
                        borderRadius="md"
                      >
                        <Checkbox isChecked={isSelected} colorScheme="limeShade" pointerEvents="none" size="md" />
                        <Box as="span" color="gray.500" display="inline-flex" alignItems="center">
                          {getFileIcon(file)}
                        </Box>
                        <Text fontSize="sm" noOfLines={1}>
                          {file.filename || file.name || file.id}
                        </Text>
                      </HStack>
                    );
                  })}
                </VStack>
              </Box>
              <HStack spacing={3} mt={2} justify="space-between">
                <Button variant="link" size="sm" color="limeShade.600" fontWeight="normal" onClick={gotoUpload}>
                  + Add new source
                </Button>
                {activeFiles.current.length > 5 && (
                  <HStack spacing={3}>
                    <Button
                      variant="link"
                      size="xs"
                      color="gray.500"
                      fontWeight="normal"
                      onClick={() => setSourceIds(activeFiles.current.map((f) => f.id))}
                    >
                      Select all
                    </Button>
                    <Button
                      variant="link"
                      size="xs"
                      color="gray.500"
                      fontWeight="normal"
                      onClick={() => setSourceIds([])}
                    >
                      Deselect all
                    </Button>
                  </HStack>
                )}
              </HStack>
            </Collapse>
          </Box>
        </HStack>
      </Box>

      {/* Other Rounds */}
      <Box
        mb={3}
        p={4}
        borderWidth="1px"
        borderColor={otherRoundsEnabled ? "lime" : "gray.200"}
        borderRadius="xl"
        bg="white"
        transition="border-color 0.2s"
      >
        <HStack spacing={3} align="flex-start">
          <Switch
            size="sm"
            mt="5px"
            colorScheme="limeShade"
            isChecked={otherRoundsEnabled}
            onChange={(e) => setOtherRoundsEnabled(e.target.checked)}
          />
          <Box flex={1}>
            <Text fontSize="body.m" fontWeight="bold">
              Other Rounds
              {otherRoundsEnabled && sourceRoundIds?.length > 0 && (
                <Text as="span" fontWeight="normal" color="gray.500">
                  {" "}
                  ({sourceRoundIds.length})
                </Text>
              )}
            </Text>
            <Text fontSize="sm" color="gray.500">
              Reference answers from previous rounds or frameworks
            </Text>
            <Collapse in={otherRoundsEnabled} animateOpacity>
              <Box mt={3}>
                <SourcesRoundsAsSourceSelector
                  currentRoundId={roundSelector?.selectedRound?.id}
                  selectedRounds={sourceRoundIds}
                  setSelectedRounds={setSourceRoundIds}
                  allowedRoundIds={questionnaireData?.sourceRoundIds}
                  hideAccordion={true}
                  hideHeader={true}
                />
              </Box>
              {sourceRoundIds?.length > 0 && (
                <Box mt={4}>
                  <Text fontSize="sm" fontWeight="600" color="gray.600" mb={2}>
                    In status
                  </Text>
                  <Wrap spacing={2}>
                    {ROUND_STAGE_OPTIONS.map((stage) => {
                      const hasQuestions = presentRoundStages.has(stage.value);
                      const isSelected = roundStages.includes(stage.value);
                      return (
                        <WrapItem key={stage.value}>
                          <Tag
                            size="sm"
                            borderRadius="full"
                            bg={isSelected && hasQuestions ? "lightLime" : "gray.100"}
                            border="1px solid"
                            borderColor={isSelected && hasQuestions ? "lime" : "gray.300"}
                            color={isSelected && hasQuestions ? "inherit" : "gray.400"}
                            px={3}
                            py={1}
                            cursor={hasQuestions ? "pointer" : "not-allowed"}
                            opacity={hasQuestions ? 1 : 0.5}
                            onClick={() => hasQuestions && handleToggleRoundStage(stage.value)}
                          >
                            <TagLabel>{stage.label}</TagLabel>
                            <Box as="span" ml={1} fontSize="xs">
                              {isSelected && hasQuestions ? "✕" : "+"}
                            </Box>
                          </Tag>
                        </WrapItem>
                      );
                    })}
                  </Wrap>
                </Box>
              )}
            </Collapse>
          </Box>
        </HStack>
      </Box>
    </Box>
  );
}
